import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/modal/Modal";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import styled from "styled-components";
import { Rounded } from "../../../theme/Rounded";
import { Info } from "../../../components/Info";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import { Breakpoints } from "../../../theme/Breakpoints";
import { appSliceSelectors } from "../../../redux/app/app.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import isUndefined from "lodash/isUndefined";
import { AtiraSelect } from "../../../components/AtiraSelect";
import camelCase from "lodash/camelCase";
import { appActions } from "../../../redux/app/app.slice";
import { SearchKind } from "../../../model/search/types/SearchKind.enum";

const InputWrapper = styled(Flex)`
  border-radius: ${Rounded.md};
  overflow: hidden;

  *:not(.ant-select-selector) {
    border-radius: 0 !important;
  }

  input {
    direction: ltr !important;
  }

  html[dir="rtl"] & input {
    direction: rtl !important;
  }
`;

const SearchSelectWrapper = styled(Flex)`
  .ant-select {
    width: 6rem !important;
  }

  .ant-select-selector {
    border-radius: 0;
    border-inline-end: 0;
    border-start-start-radius: ${Rounded.md};
    border-end-start-radius: ${Rounded.md};
  }
`;

type SearchModalProps = {
  open: boolean;
  onClose: VoidFunction;
};

export const SearchModal: React.FC<SearchModalProps> = ({
  open = false,
  onClose,
}) => {
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    undefined,
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { width } = useWindowDimensions();

  const searchPageSearchOptions = useAppSelector(
    appSliceSelectors.selectSearchOptions,
  );

  const isOnSearchPage = location.pathname.includes("/search");

  const handleSearch = () => {
    if (searchKeyword?.trim()) {
      dispatch(
        appActions.setSearchOptions({
          keyword: searchKeyword,
          kind: searchPageSearchOptions?.kind || SearchKind.ALL,
        }),
      );
      navigate("/search");
      setSearchKeyword(undefined);
    }

    onClose();
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const onChangeSearchEntityKind = (value: SearchKind) => {
    dispatch(
      appActions.setSearchOptions({ keyword: searchKeyword, kind: value }),
    );
  };

  useEffect(() => {
    if (!isOnSearchPage) {
      setSearchKeyword("");
    }
  }, [isOnSearchPage]);

  if (width > Number(Breakpoints.DESKTOP.replace("px", ""))) {
    return null;
  }

  return (
    <Modal title={t("search.title")} open={open} onClose={onClose}>
      <Flex flexDirection="column" gap="m" width={"100%"}>
        <Info info={t("search.info")} />

        <InputWrapper>
          <SearchSelectWrapper>
            <AtiraSelect
              options={Object.values(SearchKind).map((v) => ({
                label: t(`common.${camelCase(v)}`),
                value: v,
              }))}
              value={searchPageSearchOptions?.kind}
              onChange={(v) => onChangeSearchEntityKind(v as SearchKind)}
            />
          </SearchSelectWrapper>

          <Input
            placeholder={t("search.modal.input.placeholder")}
            value={
              isOnSearchPage && isUndefined(searchKeyword)
                ? searchPageSearchOptions?.keyword
                : searchKeyword
            }
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />

          <Button onClick={handleSearch} margin="none">
            {t("common.search")}
          </Button>
        </InputWrapper>
      </Flex>
    </Modal>
  );
};
